import { MarketCardDisplayLabels } from '@src/components/MarketDataCard/MarketCardDisplayLabels';
import { CardStatusIndicator } from '@src/components/StatusIndicator';
import { MarketDataStatusTable } from '@src/components/StatusTable';
import {
  HStack,
  Icon,
  IconName,
  MarketBrandLogo,
  MarketDataCardHeader,
  Popover,
  Tooltip,
  useOverflowHandler,
  usePopoverState,
  type MarketDataSnapshot,
} from '@talos/kyoko';
import type { ReactNode } from 'react';
import type { Observable } from 'rxjs';

import { HeaderCategory, HeaderEventIcon, HeaderEventTitle, HeaderEventTitleRow } from './styles';

interface PredictionCardHeaderProps {
  isDraggable: boolean;
  title: string;
  subtitle?: string;
  statusSnapshots?: Observable<MarketDataSnapshot>;
  selectedMarkets: string[];
  selectedMarketAccounts?: string[];
  /** Venue whose logo replaces the generic event icon; undefined keeps the calendar. */
  logoMarket?: string;
  menu: ReactNode;
}

export function PredictionCardHeader({
  isDraggable,
  title,
  subtitle,
  statusSnapshots,
  selectedMarkets,
  selectedMarketAccounts = [],
  logoMarket,
  menu,
}: PredictionCardHeaderProps) {
  const { isOverflowing: isTitleOverflowing, ref: titleRef } = useOverflowHandler();
  const { isOverflowing: isSubtitleOverflowing, ref: subtitleRef } = useOverflowHandler({ skip: !subtitle });
  const popover = usePopoverState({
    usePortal: true,
    trigger: 'hover',
    placement: 'bottom',
    isSmall: true,
    delay: undefined,
  });

  const statusIndicator = statusSnapshots ? (
    selectedMarkets.length || selectedMarketAccounts.length ? (
      <span data-testid="market-status-popover">
        <Popover {...popover}>
          <CardStatusIndicator
            selectedMarkets={selectedMarkets}
            selectedMarketAccounts={selectedMarketAccounts}
            snapshots={statusSnapshots}
          />
          <MarketDataStatusTable snapshots={statusSnapshots} />
        </Popover>
      </span>
    ) : (
      <CardStatusIndicator
        selectedMarkets={selectedMarkets}
        selectedMarketAccounts={selectedMarketAccounts}
        snapshots={statusSnapshots}
      />
    )
  ) : null;

  return (
    <MarketDataCardHeader
      isDraggable={isDraggable}
      slotFlexRule="1"
      topLeftItems={<MarketCardDisplayLabels />}
      topCenterItems={
        <HeaderEventTitleRow data-testid="prediction-card-header-title-row">
          <HeaderEventIcon aria-hidden data-testid="prediction-card-header-icon">
            {logoMarket ? (
              <MarketBrandLogo
                market={logoMarket}
                title={logoMarket}
                size={16}
                data-testid="prediction-card-header-market-logo"
              />
            ) : (
              <Icon icon={IconName.Calendar} size={16} />
            )}
          </HeaderEventIcon>
          <Tooltip
            delay={500}
            placement="bottom"
            tooltip={title}
            trigger={isTitleOverflowing ? 'hover' : ''}
            targetStyle={{ flex: 1, minWidth: 0 }}
          >
            <HeaderEventTitle ref={titleRef} data-testid="prediction-card-header-title">
              {title}
            </HeaderEventTitle>
          </Tooltip>
        </HeaderEventTitleRow>
      }
      topRightItems={
        <HStack m="-spacing4" gap="0" data-testid="prediction-card-header-actions">
          {statusIndicator}
          {menu}
        </HStack>
      }
      topSubheader={
        subtitle ? (
          <Tooltip
            delay={500}
            placement="bottom"
            tooltip={subtitle}
            trigger={isSubtitleOverflowing ? 'hover' : ''}
            targetStyle={{ minWidth: 0 }}
          >
            <HeaderCategory ref={subtitleRef} data-testid="prediction-card-header-subtitle">
              {subtitle}
            </HeaderCategory>
          </Tooltip>
        ) : null
      }
    />
  );
}
